import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Tabs } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useGetOrganization } from "../../../../../api/v1";
import SideDetail from "../SideDetail";
import BreadCrumb from "../../../../atoms/BreadCrumb";
import DashboardLayout from "../../../../molecules/DashboardLayout";
import { getSingleUnit } from "../ducks/actions";
import { useLocation } from "react-router-dom";
import UnitInfo from "../UnitInfo";
import NewPaymentPlan from "../NewPaymentPlan";
import { ImagesAndQrCode } from "../../../../organism/ImagesAndQrCode";
import Marketing from "../Marketing";
import { createMarketingSlug } from "../Marketing/marketingUrlUtils";
import { getSinglePropertyDetail } from "../../ducks/actions";

const { TabPane } = Tabs;

export default () => {
  const dispatch: any = useDispatch();
  const location = useLocation();
  const parentId = location.pathname?.split("/")[2];
  const unitId = location.pathname?.split("/")[4];
  const { organization } = useGetOrganization({ staleTimeInfinity: true });
  const isMarketingPublicListEnabled = Boolean(organization?.enableMarketingPublicList);
  const [activeTabKey, setActiveTabKey] = useState("1");
  const [parentPropertySlug, setParentPropertySlug] = useState("");

  let unitDetailsApi: any = {};

  unitDetailsApi = useSelector((state: any) => state.propertyDetails.unitData);

  const unitInStoreMatchesRoute =
    Boolean(unitDetailsApi?._id && unitId) && String(unitDetailsApi._id) === String(unitId);

  const refreshUnitDetails = useCallback(() => {
    if (!unitId) return;
    dispatch(getSingleUnit(unitId));
  }, [unitId, dispatch]);

  useEffect(() => {
    refreshUnitDetails();
  }, [refreshUnitDetails]);

  useEffect(() => {
    if (!parentId || !isMarketingPublicListEnabled) return;

    let cancelled = false;
    getSinglePropertyDetail(parentId)
      .then((detail: any) => {
        if (cancelled) return;
        const savedSlug = detail?.marketingData?.slug || detail?.customFields?.slug || "";
        setParentPropertySlug(String(savedSlug || ""));
      })
      .catch(() => {
        if (cancelled) return;
        setParentPropertySlug("");
      });

    return () => {
      cancelled = true;
    };
  }, [parentId, isMarketingPublicListEnabled]);

  const onUpdate = () => {
    // dispatch(getSingleUnit(unitId));
  };

  const fullUnitName = unitDetailsApi?.unit ? location.state?.parentname + " " + unitDetailsApi?.unit.unitCode : "";

  const unitPropertySlug = useMemo(() => {
    const savedSlug = String(
      parentPropertySlug ||
        unitDetailsApi?.propertySlug ||
        unitDetailsApi?.propertyMarketingData?.slug ||
        unitDetailsApi?.marketingData?.propertySlug ||
        "",
    ).trim();

    if (savedSlug) {
      // If somehow a full unit path was stored, keep only the property segment.
      return savedSlug.includes("/") ? savedSlug.split("/")[0] : savedSlug;
    }

    // Fallback: slugify propertyName from unit detail API
    return createMarketingSlug(
      String(
        unitDetailsApi?.propertyName ||
          unitDetailsApi?.buildingName ||
          location.state?.parentname ||
          "",
      ),
    );
  }, [
    parentPropertySlug,
    unitDetailsApi?.propertySlug,
    unitDetailsApi?.propertyMarketingData?.slug,
    unitDetailsApi?.marketingData?.propertySlug,
    unitDetailsApi?.propertyName,
    unitDetailsApi?.buildingName,
    location.state?.parentname,
  ]);

  const breadcrumbList: any = [
    {
      label: "Properties",
      route: "/properties",
    },
    {
      label: location.state?.parentname,
      route: `/property-detail/${parentId}`,
    },
    {
      label: fullUnitName ?? "",
      route: "",
    },
  ].filter((item) => item);

  return (
    <DashboardLayout>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <BreadCrumb list={breadcrumbList} />
        </Col>
      </Row>

      <Row gutter={24} className="p_detail_tabs">
        <Col span={24}>
          {unitInStoreMatchesRoute && (
            <SideDetail
              propertyDetails={unitDetailsApi}
              refreshDetails={refreshUnitDetails}
              fullUnitName={fullUnitName}
              isUnit={true}
              parentName={location.state?.parentname}
            />
          )}
        </Col>
        <Col span={24}>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Card bordered={false} className="ag-nospace-head ag-nospace-body h-auto">
                <Tabs
                  defaultActiveKey="1"
                  activeKey={activeTabKey}
                  onChange={setActiveTabKey}
                  animated={false}
                  tabBarGutter={16}
                  style={{ overflow: "unset" }}
                >
                  <TabPane key="1" tab="Unit Information">
                    {unitInStoreMatchesRoute && (
                      <UnitInfo propertyDetails={unitDetailsApi} parentId={parentId} updateApi={onUpdate} />
                    )}
                  </TabPane>
                  {(location?.state?.usageType === "Residential" ||
                    location?.state?.usageType === "residential") && (
                    <TabPane key="2" tab="Payment Plan">
                      {unitInStoreMatchesRoute && (
                        <NewPaymentPlan propertyDetails={unitDetailsApi} updateApi={onUpdate} />
                      )}
                    </TabPane>
                  )}

                  {unitInStoreMatchesRoute && isMarketingPublicListEnabled && (
                    <TabPane key="3" tab="Marketing">
                      <Marketing
                        entity="unit"
                        isActive={activeTabKey === "3"}
                        usageType={unitDetailsApi?.unit?.usageType || unitDetailsApi?.unit?.usagesType}
                        unitName={unitDetailsApi?.unit?.unitCode}
                        propertySlug={unitPropertySlug}
                        marketingPublicListUrl={organization?.marketingPublicListUrl}
                        propertyDetails={unitDetailsApi}
                        uploadPathSegments={{
                          propertyName:
                            unitDetailsApi?.propertyName ||
                            location.state?.parentname ||
                            unitDetailsApi?.buildingName,
                          unitCode: unitDetailsApi?.unit?.unitCode,
                        }}
                        onSaveSuccess={refreshUnitDetails}
                      />
                    </TabPane>
                  )}

                  {unitInStoreMatchesRoute && isMarketingPublicListEnabled && (
                    <TabPane key="4" tab="Images & QR Code">
                      <ImagesAndQrCode
                        assetId={unitDetailsApi?.unitId || unitId}
                        idField="unitId"
                        galleryImages={unitDetailsApi?.galleryImages || []}
                        qrValue={
                          unitDetailsApi?.marketingData?.qrCodeUrl ||
                          unitDetailsApi?.customFields?.qrCodeUrl ||
                          organization?.marketingPublicListUrl ||
                          ""
                        }
                        imagesTitle="Unit Images"
                        qrTitle="Unit QR Code"
                        contentPurpose="unit_photo"
                        qrFileNamePrefix={(fullUnitName || "unit")
                          .toString()
                          .replace(/\s+/g, "-")
                          .toLowerCase()}
                        uploadPathSegments={{
                          propertyName:
                            unitDetailsApi?.propertyName ||
                            location.state?.parentname ||
                            unitDetailsApi?.buildingName,
                          unitCode: unitDetailsApi?.unit?.unitCode,
                        }}
                        onSaveSuccess={refreshUnitDetails}
                      />
                    </TabPane>
                  )}
                </Tabs>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </DashboardLayout>
  );
};
